import React, { createContext, useState, useEffect, useContext } from "react";
import { useSnackbar } from "notistack";

import Loading from "../components/Loading";

import { Context as AuthContext } from "./AuthContext";

import { api } from "../services/Api";

import SalvaLog from "../utils/SalvaLog";

const Context = createContext();

function PermissoesProvider({ children }) {
  const [permissoes, setPermissoes] = useState([]);
  const [permissoesSet, setPermissoesSet] = useState(new Set());
  const [todasPermissoes, setTodasPermissoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { authenticated } = useContext(AuthContext);

  useEffect(() => {
    (async () => {
      if (authenticated) {
        const username = localStorage.getItem("@DNACenter:codigoUsuario");

        setLoading(true);
        try {
          const {
            data: {
              usuario: { id },
            },
          } = await api.get(`/v2/controleAcesso/usuario?username=${username}`);
          const {
            data: { permissoes: permissoesUsuario },
          } = await api.get(`/v2/controleAcesso/usuario/${id}/permissoes`);
          
          setPermissoes(permissoesUsuario);

          const permissoesSetNovo = new Set(permissoesUsuario.map(p => p.codigo));
          setPermissoesSet(permissoesSetNovo);

          const {
            data: { modulos },
          } = await api.get(`/v2/controleAcesso/modulosEPermissoes`);
          setTodasPermissoes(modulos);
        } catch (e) {
          // descomentar quando implementar o controle de acesso de maneira mais geral
          /*
          enqueueSnackbar(
            "Não foi possível recuperar suas permissões. Suas ações serão limitadas.",
            {
              variant: "error",
              autoHideDuration: 3000,
            }
          );
          */
          console.error(`Erro ao carregar permissões:`, e);
        }
        setLoading(false);
      } else {
        setPermissoes([]);
        setPermissoesSet(new Set());
      }
    })();
  }, [authenticated, reload, enqueueSnackbar]);

  // checa permissao e se tiver, executa uma acao
  const checarPermissaoComAcao = (codigo, acao) => {
    const temPermissao = permissoesSet.has(codigo);
    if (temPermissao) {
      acao();
      return;
    }

    enqueueSnackbar(`Você não tem permissão para realizar esta ação. Código: ${codigo}`, {
      variant: "error",
      autoHideDuration: 3000,
    });
  };

  // somente checa uma permissao
  const checarPermissao = (codigo, log = true) => {
    const permissaoExiste = permissoesSet.has(codigo);
    
    if (permissaoExiste) {
      if (log) {
        const permissaoCompleta = permissoes.find(p => p.codigo === codigo && p.modulo);
        if (permissaoCompleta) {
          SalvaLog(permissaoCompleta.nome, permissaoCompleta.modulo.nome, `Tem permissão: ${codigo}`);
        }
      }
      return true;
    }
    
    if (log) {
      const permissaoTentada = todasPermissoes.find(p => p.codigo === codigo && p.modulo);
      if (permissaoTentada) {
        SalvaLog(
          permissaoTentada.nome,
          permissaoTentada.modulo.nome,
          `Tentou usar permissão: ${codigo}`
        );
      }
    }
    return false;
  };

  const checarPermissaoSemLog = codigo => checarPermissao(codigo, false);

  const sobreescreverPermissoes = novasPermissoes => {
    setPermissoes(novasPermissoes);
    setPermissoesSet(new Set(novasPermissoes.map(p => p.codigo)));
  };

  const atualizarPermissoes = (permissoesModuloModificadas, idModulo) => {
    // todas as permissoes que nao sao desse modulo
    const permissoesOutrosModulos = permissoes.filter(
      permissao => parseInt(permissao.id_modulo, 10) !== parseInt(idModulo, 10)
    );
    const novasPermissoes = [...permissoesOutrosModulos, ...permissoesModuloModificadas];
    setPermissoes(novasPermissoes);
    setPermissoesSet(new Set(novasPermissoes.map(p => p.codigo)));
  };

  return (
    <Context.Provider
      value={{
        checarPermissaoComAcao,
        permissoes,
        checarPermissao,
        sobreescreverPermissoes,
        atualizarPermissoes,
        checarPermissaoSemLog,
        setReload,
      }}
    >
      {loading && <Loading />}
      {children}
    </Context.Provider>
  );
}

export { Context, PermissoesProvider };
